import { useRef, useState } from 'react';

import { SearchSettings } from '@givebacks/api/query';
import { ComboboxItem } from 'ui';

import { SearchableMultiSelect } from '../../../../src/components/molecules/inputs/SearchableInputs/SearchableMultiSelect';
import { SearchableSelect } from '../../../../src/components/molecules/inputs/SearchableInputs/SearchableSelect';
import { flattenSearchData } from '../../../../src/components/molecules/inputs/SearchableInputs/utils/flattenSearchData';
import { useTableSearch } from '../../Search/useTableSearch';
import { useTableContext } from '../../table.context';
import {
  TableSearchableMultiSelectFieldProps,
  TableSearchableSelectFieldProps,
} from '../../types/searchFieldsTypes';
import { deserializeMultiSelect } from './deSerializers/deSeralizers';

const SearchableSelectField = <T extends SearchSettings>({
  fields,
  rootRef,
  initialValue,
  comboboxProps,
  showInParams = true,
  ...rest
}: TableSearchableSelectFieldProps<T>) => {
  const ctx = useTableContext();
  const [search, setSearch] = useState<null | string>(initialValue ?? rest.defaultValue ?? null);
  const dataRef = useRef<TableSearchableMultiSelectFieldProps<T>['data']>();

  useTableSearch({
    search,
    store: ctx.atomStore,
    reset: () => setSearch(null),
    serializer: 'string',
    label: rest.label,
    fields,
    showInParams,
    customFormatter: (value) => {
      const data = dataRef.current;
      const foundItem = flattenSearchData(data ?? []).find((item) => {
        if (typeof item === 'object' && 'value' in item) {
          return item?.value === value;
        }
        return item === value;
      });

      const label =
        typeof foundItem === 'object' && 'value' in foundItem ? foundItem?.label : foundItem;
      return typeof label == 'string' ? label : value;
    },
  });
  return (
    <SearchableSelect
      {...rest}
      onChange={setSearch}
      value={search}
      comboboxProps={{
        ...comboboxProps,
        portalProps: { target: rootRef?.current ? rootRef?.current : undefined },
      }}
      dataRef={dataRef}
    />
  );
};

const SearchableMultiSelectField = <T extends SearchSettings>({
  fields,
  rootRef,
  showInParams = true,
  initialValue,
  searchBy,
  ...rest
}: TableSearchableMultiSelectFieldProps<T>) => {
  const ctx = useTableContext();
  const [search, setSearch] = useState<string[]>(
    deserializeMultiSelect(initialValue) ?? rest.defaultValue ?? []
  );
  const dataRef = useRef<TableSearchableMultiSelectFieldProps<T>['data']>();
  const multiSelectDataRef = useRef<ComboboxItem[]>([]);

  useTableSearch({
    search,
    store: ctx.atomStore,
    reset: () => setSearch([]),
    fields,
    searchBy,
    serializer: 'multi-select',
    label: rest.label,
    showInParams,
    customFormatter: (value) => {
      const valueArray = value.split(',');

      const label = valueArray.map((selectedValue) => {
        const data = multiSelectDataRef.current;
        const foundItem = data?.find((item) => {
          if (typeof item === 'object' && 'value' in item) {
            return item?.value === selectedValue;
          }
          return item === selectedValue;
        });

        const label =
          typeof foundItem === 'object' && 'value' in foundItem ? foundItem?.label : foundItem;
        return typeof label == 'string' ? label : selectedValue;
      });
      return label.join(',');
    },
  });

  return (
    <SearchableMultiSelect
      onChange={setSearch}
      {...rest}
      value={search}
      comboboxProps={{
        ...rest.comboboxProps,
        portalProps: { target: rootRef?.current ? rootRef?.current : undefined },
      }}
      dataRef={dataRef}
      multiSelectDataRef={multiSelectDataRef}
    />
  );
};

export { SearchableMultiSelectField, SearchableSelectField };
